import { CurrencyPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  computed,
  effect,
  signal,
  untracked,
  viewChild,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';

import {
  AdvancePaymentController,
  PaymentDeleteConfirmationState,
} from '@shared-classes';
import {
  VOneTabsComponent,
  VOneInputComponent,
  VOneButtonComponent,
  VOneIconComponent,
} from '@shared-components';
import { PAYMENT_MANAGER_ROLES, PAYMENT_TAB_ID } from '@shared-constants';
import { StickyHeaderHeightDirective } from '@shared-directives';
import {
  AdvancePaymentAction,
  AdvancePaymentOwnerType,
  AdvancePaymentSubject,
  IconType,
  PaymentEntryType,
} from '@shared-enums';
import {
  GraphQLErrorResponse,
  LocalPaymentEntry,
  SelectOption,
  VOneTab,
} from '@shared-models';
import {
  advancePaymentTotal as advancePaymentTotalUtil,
  APPLIED_PAYMENT_STATUS,
  appliedPendingBalance as appliedPendingBalanceUtil,
  calculateAvailable,
  canDeletePayment as canDeletePaymentRule,
  findActiveCancellationId,
  getTodayIsoDate,
  toStoredDateString,
  hasAdvancePayment as hasAdvancePaymentUtil,
  DOCUMENT_STATE,
  hasAssociatedPayments as hasAssociatedPaymentsUtil,
  hasMultipleAdvancePayments as hasMultipleAdvancePaymentsUtil,
  INVOICE_BALANCE_EPSILON,
  isFullyPaid as isFullyPaidUtil,
  isInvoiceVoided,
  isPaymentApproved,
  round2,
  sortPaymentsByDate,
  sumAppliedPayments,
  valueMatchesRoundedTarget,
} from '@shared-utils';

import {
  IPaymentsList,
  IPurchasePayments,
} from 'src/app/@interfaces/payments.interface';
import {
  IPurchaseInvoice,
  IPurchasesInvoices,
} from 'src/app/@interfaces/purchaseInvoice.interface';
import { ITreasury } from 'src/app/@interfaces/treasury.interface';
import { AccountsPayableService } from 'src/app/@pages/accounts-payable/accounts-payable.service';
import { PurchasesService } from 'src/app/@pages/purchases/purchases.service';
import { PayPurchasePdfService } from 'src/app/@shared/components/dialog/crud-purchase/pay-purchase/pay-purchase-pdf.service';
import { PayPurchaseService } from 'src/app/@shared/components/dialog/crud-purchase/pay-purchase/pay-purchase.service';
import { PaymentsPdfService } from 'src/app/@shared/components/dialog/crud-quotation/payments/payments-pdf.service';
import { PaymentsService } from 'src/app/@shared/components/dialog/crud-quotation/payments/payments.service';
import { PdfGeneratorService } from 'src/app/@shared/components/pdf-generator/pdf-generator.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { AlertService } from 'src/app/uplift/shared/services/alert/alert.service';

@Component({
  selector: 'v-one-purchase-payment-modal',
  templateUrl: './purchase-payment-modal.component.html',
  styleUrls: [
    './purchase-payment-modal.component.scss',
    './purchase-payment-modal.component.responsive.scss',
  ],
  imports: [
    VOneTabsComponent,
    VOneInputComponent,
    VOneButtonComponent,
    VOneIconComponent,
    ReactiveFormsModule,
    CurrencyPipe,
    DatePipe,
    NgTemplateOutlet,
    TranslatePipe,
    StickyHeaderHeightDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasePaymentModalComponent implements OnInit, OnDestroy {
  // Not `private` — AdvancePaymentController takes `this` typed as
  // AdvancePaymentHost, and TypeScript treats a private member as
  // incompatible with any other type (interface included) that declares the
  // same-named property without that exact private modifier.
  translate = inject(TranslateService);
  private accountsPay = inject(AccountsPayableService);
  private purchasesService = inject(PurchasesService);
  private spinnerService = inject(NgxSpinnerService);
  private payPurchasePdfService = inject(PayPurchasePdfService);
  private paymentsPdfService = inject(PaymentsPdfService);
  private pdfGeneratorService = inject(PdfGeneratorService);
  private payPurchaseService = inject(PayPurchaseService);
  paymentsService = inject(PaymentsService);
  private alertService = inject(AlertService);
  private cookieAuthService = inject(CookieAuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly idInvoice = input(0);
  readonly idProvider = input(0);
  readonly invoicePayments = input<IPurchasePayments[]>([]);
  readonly initialTabId = input<string>(PAYMENT_TAB_ID.REGISTER);
  readonly onClose = input<
    | ((result: {
        sended: boolean;
        refresh: boolean;
        notifyState: number | null;
      }) => void)
    | undefined
  >(undefined);
  readonly onPaymentRegistered = input<(() => void) | undefined>(undefined);
  readonly closeModal = input<(() => void) | undefined>(undefined);

  readonly currentInvoicePayments = signal<IPurchasePayments[]>([]);

  readonly listBanks = signal<IPaymentsList[]>([]);
  readonly listCashiers = signal<IPaymentsList[]>([]);
  readonly advancePayments = signal<ITreasury[]>([]);

  readonly purchase = signal({} as IPurchaseInvoice | IPurchasesInvoices);
  readonly refundsTotal = signal(0);
  readonly invoiceTotalLoaded = signal(false);
  readonly totalInvoice = computed(() => {
    const invoiceObject = this.purchase() as IPurchaseInvoice;
    const lineTotal = invoiceObject.purchases?.[0]?.total ?? 0;
    return round2(
      lineTotal -
        this.refundsTotal() -
        (invoiceObject.withholdingTax ?? 0) -
        (invoiceObject.icaTax ?? 0),
    );
  });
  readonly paidInvoice = signal(0);
  readonly missingPaid = computed(() =>
    Math.max(0, round2(this.totalInvoice() - this.paidInvoice())),
  );
  sended = false;
  refresh = false;
  // Whether a payment was applied during this session, and the state the
  // purchase ended up in. Only a session that applied one notifies accounts
  // payable — a deletion that happens to leave it partially paid is not
  // news.
  private paymentRegistered = false;
  // The payment state as the server resolved it. The modal used to derive
  // its own from the balance, which is how it ended up disagreeing with the
  // card. Null only while the purchase has not loaded — the getters below
  // fall back to the old derivation there.
  readonly documentState = signal<number | null>(null);

  @ViewChild('registerTab', { static: true })
  registerTab!: TemplateRef<unknown>;
  @ViewChild('historyTab', { static: true })
  historyTab!: TemplateRef<unknown>;
  @ViewChild('invoiceDetailTab', { static: true })
  invoiceDetailTab!: TemplateRef<unknown>;

  readonly stickyHeader = viewChild(StickyHeaderHeightDirective);

  tabs = signal<VOneTab[]>([]);
  protected readonly iconType = IconType;
  protected readonly appliedPaymentStatus = APPLIED_PAYMENT_STATUS;

  readonly paymentMethodControl = new FormControl<number | null>(null);
  readonly bankOrCashierControl = new FormControl<number | null>(null);
  readonly paymentValueControl = new FormControl<string | number | null>(null);
  readonly advanceValueControl = new FormControl<string | number | null>(null);
  readonly payFullAmount = signal<boolean>(false);
  readonly dateControl = new FormControl<string>(getTodayIsoDate());
  readonly referenceControl = new FormControl<string>('');

  readonly addedPayments = signal<LocalPaymentEntry[]>([]);
  private nextLocalPaymentId = 0;

  readonly isSubmittingPayments = signal(false);

  readonly useAdvancePayment = signal<boolean>(false);
  readonly selectedAdvancePaymentId = signal<number | null>(null);
  readonly useFullAdvanceAvailable = signal<boolean>(false);

  private readonly deleteState = new PaymentDeleteConfirmationState();
  private readonly advancePayment = new AdvancePaymentController(this);

  constructor() {
    this.paymentValueControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.syncPayFullAmountWithValue(value);
        this.paymentValueControl.setValue(value, { emitEvent: false });
      });

    this.advancePayment.mirrorValueControls(this.destroyRef);

    effect(() => {
      if (
        !this.useAdvancePayment() ||
        !this.useFullAdvanceAvailable() ||
        this.selectedAdvancePaymentId() === null
      ) {
        return;
      }
      const fullAmount = this.selectedAdvancePaymentAvailable;
      untracked(() => {
        if (
          !valueMatchesRoundedTarget(this.advanceValueControl.value, fullAmount)
        ) {
          this.advanceValueControl.setValue(fullAmount);
        }
      });
    });

    this.paymentMethodControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.useAdvancePayment()) {
          this.bankOrCashierControl.reset(null);
        }
      });
  }

  private paidInvoiceTabsOnly = false;

  async ngOnInit(): Promise<void> {
    this.currentInvoicePayments.set(this.invoicePayments());
    this.buildTabs();

    try {
      const [{ invoice, refundsTotal }] = await Promise.all([
        this.payPurchaseService.getInvoiceWithRefunds(this.idInvoice()),
        this.listsData(),
      ]);
      this.purchase.set(invoice);
      this.documentState.set(invoice?.state_invoice ?? null);
      this.refundsTotal.set(refundsTotal);
      this.invoiceTotalLoaded.set(true);
      this.paidInvoice.set(
        this.currentInvoicePayments().reduce(
          (total, item) => total + item.value,
          0,
        ),
      );

      this.paidInvoiceTabsOnly = this.isFullyPaid;

      await this.getAdvancePayments();
    } catch (error: unknown) {
      const message =
        (error as GraphQLErrorResponse)?.graphQLErrors?.[0]?.message ||
        (error as Error)?.message ||
        this.translate.instant('global.alerts.title.error');
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message,
        type: 'error',
        acceptText: 'Ok',
      });
    } finally {
      this.buildTabs();
    }
  }

  private buildTabs(): void {
    const tabs: VOneTab[] = [];
    if (!this.paidInvoiceTabsOnly) {
      tabs.push({
        id: PAYMENT_TAB_ID.REGISTER,
        label: this.translate.instant('global.payments.register-tab'),
        content: this.registerTab,
      });
    }
    tabs.push({
      id: PAYMENT_TAB_ID.HISTORY,
      label: this.translate.instant('global.payments.history-tab'),
      content: this.historyTab,
    });
    if (this.currentInvoicePayments().length > 0 || this.isVoidedInvoice) {
      tabs.push({
        id: PAYMENT_TAB_ID.INVOICE_DETAIL,
        label: this.translate.instant('global.payments.detail-tab'),
        content: this.invoiceDetailTab,
      });
    }
    this.tabs.set(tabs);
  }

  private async listsData(): Promise<void> {
    const [listCashiers, listBanks] = await Promise.all([
      this.paymentsService.getCashierList(),
      this.paymentsService.getBankList(),
    ]);
    this.listCashiers.set(listCashiers);
    this.listBanks.set(listBanks);
  }

  private async getAdvancePayments(): Promise<void> {
    this.advancePayments.set(
      await this.paymentsService.getAdvancePayments(
        null,
        this.idProvider(),
        AdvancePaymentOwnerType.PROVIDERS,
      ),
    );
  }

  private async refreshInvoice(): Promise<void> {
    const purchase = await this.payPurchaseService.getInvoice(this.idInvoice());
    this.purchase.set(purchase);
    this.documentState.set(purchase?.state_invoice ?? null);
    await this.getAdvancePayments();
  }

  /**
   * Reads back only the state the purchase reached. state_invoice is derived
   * on the server, so the modal cannot know it without asking — but it must
   * not replace `purchase` here, or the totals the session is working with
   * would be recomputed mid-flight.
   */
  private async refreshReachedState(): Promise<void> {
    try {
      const purchase = await this.payPurchaseService.getInvoice(
        this.idInvoice(),
      );
      this.documentState.set(purchase?.state_invoice ?? null);
    } catch (error) {
      console.error('Error reading the purchase payment state', error);
    }
  }

  private async updatePayments(): Promise<void> {
    this.currentInvoicePayments.set(
      await firstValueFrom(
        this.accountsPay.getPaymenPurchase(this.idInvoice()),
      ),
    );
    this.buildTabs();
  }

  // Register payment

  get paymentMethodOptions(): SelectOption[] {
    return this.advancePayment.paymentMethodOptions;
  }

  get bankOrCashierOptions(): SelectOption[] {
    return this.advancePayment.bankOrCashierOptions;
  }

  get hasAdvancePayment(): boolean {
    return hasAdvancePaymentUtil(this.advancePayments());
  }

  get hasMultipleAdvancePayments(): boolean {
    return hasMultipleAdvancePaymentsUtil(this.advancePayments());
  }

  get advancePaymentTotal(): number {
    return advancePaymentTotalUtil(this.advancePayments());
  }

  onToggleAdvancePayment(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.useAdvancePayment.set(checked);

    if (checked && this.advancePayments().length === 1) {
      this.selectAdvancePayment(this.advancePayments()[0].id_treasury);
    } else {
      this.selectAdvancePayment(null);
    }
    if (!checked) {
      this.paymentValueControl.setValue(null);
    }
  }

  onSelectAdvancePayment(idTreasury: number): void {
    this.selectAdvancePayment(idTreasury);
  }

  onToggleFullAdvanceAmount(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.useFullAdvanceAvailable.set(checked);

    if (checked) {
      this.advanceValueControl.setValue(this.selectedAdvancePaymentAvailable);
    }
  }

  removeAdvancePaymentSelection(): void {
    this.useAdvancePayment.set(false);
    this.selectAdvancePayment(null);
    this.paymentValueControl.reset(null);
  }

  get selectedAdvancePaymentAvailable(): number {
    return this.advancePayment.selectedAdvancePaymentAvailable;
  }

  private selectAdvancePayment(idTreasury: number | null): void {
    this.useFullAdvanceAvailable.set(false);
    this.advancePayment.selectAdvancePayment(idTreasury);
    if (this.useAdvancePayment()) {
      return;
    }
    this.payFullAmount.set(
      this.matchesRemainingAfterBalance(this.paymentValueControl.value),
    );
  }

  private syncPaymentOriginDisplay(idPayment: number | null): void {
    this.advancePayment.syncPaymentOriginDisplay(idPayment);
  }

  get paymentMethodsDisabled(): boolean {
    return this.useAdvancePayment();
  }

  calculateAvailable(total: number, used: number): number {
    return calculateAvailable(total, used);
  }

  onPayFullAmountToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (this.useAdvancePayment()) {
      if (checked) {
        this.paymentValueControl.setValue(
          this.selectedAdvancePaymentAvailable || null,
        );
      } else if (
        valueMatchesRoundedTarget(
          this.paymentValueControl.value,
          this.selectedAdvancePaymentAvailable,
        )
      ) {
        this.paymentValueControl.setValue(null);
      }
      this.payFullAmount.set(checked);
      return;
    }
    if (checked) {
      this.paymentValueControl.setValue(this.remainingAfterBalanceApplications);
    } else if (
      this.matchesRemainingAfterBalance(this.paymentValueControl.value)
    ) {
      this.paymentValueControl.setValue(null);
    }
  }

  private syncPayFullAmountWithValue(value: string | number | null): void {
    if (this.useAdvancePayment()) {
      this.advancePayment.syncFullAdvanceFlags(value);
      return;
    }
    this.payFullAmount.set(this.matchesRemainingAfterBalance(value));
  }

  private matchesRemainingAfterBalance(value: string | number | null): boolean {
    return valueMatchesRoundedTarget(
      value,
      this.remainingAfterBalanceApplications,
    );
  }

  // Invoice balance still to pay minus the lines already added; the available
  // advance balance only counts once the user adds it.
  get remainingAfterBalanceApplications(): number {
    return Math.max(0, round2(this.missingPaid() - this.addedPaymentsTotal));
  }

  private matchesMissingPaid(value: string | number | null): boolean {
    return valueMatchesRoundedTarget(value, this.missingPaid());
  }

  private get addedPaymentsTotal(): number {
    return this.addedPayments().reduce((sum, entry) => sum + entry.value, 0);
  }

  private async ensurePaymentTypeAssigned(
    idPayment: number,
    paymentMethodType: number,
  ): Promise<void> {
    const alreadyAssigned = (this.purchase().payment ?? []).some(
      (item) => item.id_payment === idPayment,
    );
    if (alreadyAssigned) {
      return;
    }

    await firstValueFrom(
      this.accountsPay.assingPurchasePays(
        this.idInvoice(),
        idPayment,
        paymentMethodType,
      ),
    );
    await this.refreshInvoice();
  }

  async addPayment(): Promise<void> {
    const paymentMethod = this.paymentMethodOptions.find(
      (option) => option.value === this.paymentMethodControl.value,
    );
    const bankOrCashier = this.bankOrCashierOptions.find(
      (option) => option.value === this.bankOrCashierControl.value,
    );
    const idPayment = this.bankOrCashierControl.value;
    const paymentMethodType = this.paymentMethodControl.value;
    const rawValue = this.useAdvancePayment()
      ? this.advanceValueControl.value
      : this.paymentValueControl.value;
    const value =
      typeof rawValue === 'number' ? rawValue : parseFloat(rawValue ?? '');

    if (
      !paymentMethod ||
      !bankOrCashier ||
      idPayment === null ||
      paymentMethodType === null ||
      !value ||
      isNaN(value)
    ) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.ap-add-payment-missing-fields',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    if (value < 0) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.payment-value-not-positive',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    if (
      round2(this.addedPaymentsTotal + value) >
      round2(this.missingPaid()) + INVOICE_BALANCE_EPSILON
    ) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant('global.payments.ap-value-exceeds', {
          missing: this.missingPaid().toFixed(2),
        }),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    const advanceTreasury = this.useAdvancePayment()
      ? this.advancePayments().find(
          (candidate) =>
            candidate.id_treasury === this.selectedAdvancePaymentId(),
        )
      : undefined;

    const rawManualValue = this.paymentValueControl.value;
    const manualValue =
      typeof rawManualValue === 'number'
        ? rawManualValue
        : parseFloat(rawManualValue ?? '');

    if (
      advanceTreasury &&
      (value > this.selectedAdvancePaymentAvailable ||
        manualValue > this.selectedAdvancePaymentAvailable)
    ) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.advance-value-exceeds',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    this.spinnerService.show();
    try {
      await this.ensurePaymentTypeAssigned(idPayment, paymentMethodType);
    } catch {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant('global.alerts.title.error'),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    } finally {
      this.spinnerService.hide();
    }

    const registeredValue =
      advanceTreasury && manualValue > 0 ? manualValue : value;

    this.addedPayments.update((entries) => [
      ...entries,
      {
        id: `local-${this.nextLocalPaymentId++}`,
        id_payment: idPayment,
        paymentMethodType,
        paymentMethodLabel: paymentMethod.label,
        bankOrCashierLabel: bankOrCashier.label,
        value: registeredValue,
        date: this.dateControl.value ?? getTodayIsoDate(),
        id_ref: this.referenceControl.value ?? '',
        type: this.matchesMissingPaid(registeredValue)
          ? PaymentEntryType.CANCELLATION
          : PaymentEntryType.CREDIT,
        treasury: advanceTreasury,
      },
    ]);

    this.resetPaymentForm();
    if (advanceTreasury) {
      this.removeAdvancePaymentSelection();
    }
  }

  removePaymentEntry(id: string): void {
    this.addedPayments.update((entries) =>
      entries.filter((entry) => entry.id !== id),
    );
  }

  async registerPayments(): Promise<void> {
    if (this.isSubmittingPayments()) {
      return;
    }

    const entries = this.addedPayments();
    if (entries.length === 0) {
      return;
    }

    this.isSubmittingPayments.set(true);
    this.spinnerService.show();

    const remaining = [...entries];
    for (const entry of entries) {
      try {
        const result = await firstValueFrom(
          this.accountsPay.createPaymentPurchases(
            this.idInvoice(),
            entry.id_payment,
            entry.type,
            toStoredDateString(entry.date),
            entry.value,
            entry.id_ref,
          ),
        );
        if (entry.treasury) {
          await this.paymentsService.relateAdvancePayment(
            AdvancePaymentAction.SET,
            AdvancePaymentSubject.PURCHASE,
            result.id_paypurchases,
            entry.treasury.id_treasury,
            entry.value,
          );
        }
        await this.autoApprovePayment(result.id_paypurchases);
        this.paidInvoice.update((current) => current + result.value);
        remaining.shift();
      } catch (error: unknown) {
        this.spinnerService.hide();
        this.isSubmittingPayments.set(false);
        this.addedPayments.set(remaining);
        await this.updatePayments();
        await this.getAdvancePayments();
        const message =
          (error as GraphQLErrorResponse)?.graphQLErrors?.[0]?.message ||
          (error as Error)?.message ||
          this.translate.instant('global.alerts.title.error');
        this.alertService.open({
          title: this.translate.instant('payments.error.title'),
          message,
          type: 'error',
          acceptText: 'Ok',
        });
        return;
      }
    }

    this.spinnerService.hide();
    this.isSubmittingPayments.set(false);
    this.addedPayments.set([]);
    await this.updatePayments();
    await this.getAdvancePayments();
    await this.refreshReachedState();
    this.sended = true;
    this.refresh = true;
    this.paymentRegistered = true;
    this.onPaymentRegistered()?.();

    if (this.isInvoiceFullyPaid()) {
      try {
        await this.changeStatus();
      } catch {
        return;
      }
    }

    this.alertService.open({
      title: this.translate.instant('Created'),
      message: this.translate.instant('Payment created successfully!'),
      type: 'success',
      acceptText: 'Ok',
    });
  }

  private async autoApprovePayment(idPaypurchases: number): Promise<void> {
    await firstValueFrom(
      this.accountsPay.updateStatusPurchasePayment(idPaypurchases, true, false),
    );
    await firstValueFrom(
      this.accountsPay.updateStatusPurchasePayment(idPaypurchases, false, true),
    );
  }

  private async afterRegisterPayment(
    result: IPurchasePayments,
    value: number,
  ): Promise<void> {
    await this.autoApprovePayment(result.id_paypurchases);

    this.paidInvoice.update((current) => current + value);
    await this.updatePayments();
    await this.getAdvancePayments();
    await this.refreshReachedState();

    this.resetPaymentForm();
    this.sended = true;
    this.refresh = true;
    this.paymentRegistered = true;
    this.onPaymentRegistered()?.();

    if (this.isInvoiceFullyPaid()) {
      await this.changeStatus();
    }

    this.alertService.open({
      title: this.translate.instant('Created'),
      message: this.translate.instant('Payment created successfully!'),
      type: 'success',
      acceptText: 'Ok',
    });
  }

  private isInvoiceFullyPaid(): boolean {
    const rawTotalForClosing = round2(
      this.totalInvoice() + this.withholdingTax + this.icaTax,
    );
    return (
      round2(this.paidInvoice()) >= rawTotalForClosing &&
      !!this.purchase().payment &&
      this.paymentsService.validCompletePayments(this.currentInvoicePayments())
    );
  }

  private async changeStatus(): Promise<void> {
    this.spinnerService.show();
    this.purchase.set(
      await this.payPurchaseService.getInvoice(this.idInvoice()),
    );
    const paymentsDescriptions: string[] = (this.purchase().payment ?? []).map(
      (payment) => payment.description,
    );
    const getPdf = await this.payPurchasePdfService.createPaymentPurchasePdf(
      false,
      'Cancelled',
      this.purchase() as IPurchasesInvoices,
      this.paymentsPdfService.getCompanyLogo(),
      paymentsDescriptions.join(', '),
    );
    if (!getPdf) {
      this.spinnerService.hide();
      return;
    }
    await new Promise<void>((resolve, reject) => {
      getPdf.getBase64((data: string) => {
        this.purchasesService
          .updateStatusPurchase(this.idInvoice(), true, data)
          .subscribe({
            next: () => {
              this.spinnerService.hide();
              this.refresh = true;
              resolve();
            },
            error: (error: unknown) => {
              this.spinnerService.hide();
              this.alertService.open({
                title: this.translate.instant('payments.error.title'),
                message: this.translate.instant('global.alerts.title.error'),
                type: 'error',
                acceptText: 'Ok',
              });
              reject(error);
            },
          });
      });
    });
  }

  private resetPaymentForm(): void {
    this.paymentMethodControl.reset(null);
    this.bankOrCashierControl.reset(null);
    this.paymentValueControl.reset(null);
    this.dateControl.reset(getTodayIsoDate());
    this.referenceControl.reset('');
  }

  // History tab

  get sortedInvoicePayments(): IPurchasePayments[] {
    return sortPaymentsByDate(this.currentInvoicePayments());
  }

  get mostRecentPaymentId(): number | null {
    const sorted = this.sortedInvoicePayments;
    return sorted.length > 0 ? sorted[sorted.length - 1].id_paypurchases : null;
  }

  private get activeCancellationPaymentId(): number | null {
    return findActiveCancellationId(
      this.currentInvoicePayments().map((payment) => ({
        id: payment.id_paypurchases,
        status: payment.status,
        type: payment.type,
      })),
    );
  }

  canDeletePayment(idPaypurchases: number): boolean {
    return canDeletePaymentRule(
      idPaypurchases,
      this.activeCancellationPaymentId,
    );
  }

  isPendingDeleteConfirmation(idPaypurchases: number): boolean {
    return this.deleteState.isPendingConfirmation(idPaypurchases);
  }

  isDeletingPayment(idPaypurchases: number): boolean {
    return this.deleteState.isProcessing(idPaypurchases);
  }

  requestDeletePayment(idPaypurchases: number): void {
    this.deleteState.request(idPaypurchases);
  }

  cancelDeletePayment(): void {
    this.deleteState.cancel();
  }

  confirmDeletePayment(paymentDelete: IPurchasePayments): void {
    const id = paymentDelete.id_paypurchases;
    this.deleteState.beginProcessing(id);
    this.removePayment(id);
  }

  private removePayment(id: number): void {
    this.accountsPay
      .removePaymentPurchases(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (result) => {
          this.deleteState.endProcessing(id);
          if (!result) {
            this.alertService.open({
              title: this.translate.instant('payments.error.title'),
              message: this.translate.instant('global.alerts.title.error'),
              type: 'error',
              acceptText: 'Ok',
            });
            return;
          }
          this.paidInvoice.update((current) => current - result.value);
          this.alertService.open({
            title: this.translate.instant('Good'),
            message: this.translate.instant('Payment removed'),
            type: 'success',
            acceptText: 'Ok',
          });
          try {
            await this.updatePayments();
            // Re-reads the purchase, not just the advances: the backend drops
            // a payment method once the last payment using it is deleted, and
            // ensurePaymentTypeAssigned guards against a stale copy of it.
            await this.refreshInvoice();
          } catch (error) {
            console.error(
              'Error finishing payment removal after payment deletion',
              error,
            );
            this.alertService.open({
              title: this.translate.instant('payments.error.title'),
              message: this.translate.instant('global.alerts.title.error'),
              type: 'error',
              acceptText: 'Ok',
            });
          } finally {
            this.refresh = true;
          }
        },
        error: () => {
          this.deleteState.stopProcessing(id);
          this.alertService.open({
            title: this.translate.instant('payments.error.title'),
            message: this.translate.instant('global.alerts.title.error'),
            type: 'error',
            acceptText: 'Ok',
          });
        },
      });
  }

  statusLabel(status: number): string {
    return isPaymentApproved(status)
      ? this.translate.instant('global.payments.status-approved')
      : this.translate.instant('global.payments.status-pending-approval');
  }

  get canManagePayments(): boolean {
    return PAYMENT_MANAGER_ROLES.includes(
      String(this.cookieAuthService.getRolId),
    );
  }

  // Invoice detail tab

  get providerName(): string {
    return (this.purchase() as IPurchaseInvoice).provider?.nombre || '-';
  }

  get providerNit(): string {
    return (this.purchase() as IPurchaseInvoice).provider?.doc_nit || '-';
  }

  get purchaseNumber(): string {
    return (this.purchase() as IPurchaseInvoice).num_invo || '-';
  }

  get providerInvoiceNumber(): string {
    return (
      (this.purchase() as IPurchaseInvoice).contpurchase?.written_invoice || '-'
    );
  }

  get originalInvoiceTotal(): number {
    return (this.purchase() as IPurchaseInvoice).purchases?.[0]?.total ?? 0;
  }

  get withholdingTax(): number {
    return (this.purchase() as IPurchaseInvoice).withholdingTax ?? 0;
  }

  get icaTax(): number {
    return (this.purchase() as IPurchaseInvoice).icaTax ?? 0;
  }

  get appliedPaidTotal(): number {
    return sumAppliedPayments(this.currentInvoicePayments());
  }

  get appliedPendingBalance(): number {
    return appliedPendingBalanceUtil(
      this.totalInvoice(),
      this.appliedPaidTotal,
    );
  }

  get isFullyPaid(): boolean {
    // The balance fallback needs the total: payments arrive before it does,
    // and against a total of 0 any applied payment would read as Pagada.
    const state = this.documentState();
    return state === null
      ? this.invoiceTotalLoaded() &&
          isFullyPaidUtil(this.totalInvoice(), this.appliedPaidTotal)
      : state === DOCUMENT_STATE.PAID;
  }

  get hasAssociatedPayments(): boolean {
    const state = this.documentState();
    return state === null
      ? hasAssociatedPaymentsUtil(this.appliedPaidTotal)
      : state === DOCUMENT_STATE.PARTIAL || state === DOCUMENT_STATE.PAID;
  }

  get isVoidedInvoice(): boolean {
    const state = this.documentState();
    return state === null
      ? isInvoiceVoided(this.totalInvoice(), this.hasAssociatedPayments)
      : state === DOCUMENT_STATE.VOID;
  }

  protected handleCloseButtonClick(): void {
    this.closeModal()?.();
  }

  protected async generateReceipt(): Promise<void> {
    // Open before any await: mobile browsers block tabs opened outside the tap.
    const receiptWindow = this.pdfGeneratorService.openPendingWindow();

    try {
      const paymentsDescriptions = (this.purchase().payment ?? []).map(
        (payment) => payment.description,
      );

      await this.payPurchasePdfService.createPaymentPurchasePdf(
        true,
        'Cancelled',
        this.purchase() as IPurchasesInvoices,
        this.paymentsPdfService.getCompanyLogo(),
        paymentsDescriptions.join(', '),
        receiptWindow,
      );
    } catch (error) {
      console.error('Error generating payment receipt:', error);
      this.pdfGeneratorService.closePendingWindow(receiptWindow);
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.generate-receipt-error',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
    }
  }

  ngOnDestroy(): void {
    this.onClose()?.({
      sended: this.sended,
      refresh: this.refresh,
      notifyState: this.paymentRegistered ? this.documentState() : null,
    });
  }
}
